import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Header from './authentication/Header';

let squarePayments = null;
let squareCard = null;

function affixScriptToHead(url) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${url}"]`)) { resolve(); return; }
    const s = document.createElement('script');
    s.src = url;
    s.onload = resolve;
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

function getUniqueCards(cards) {
  const map = new Map();
  (cards || []).forEach(c => {
    const key = `${c.exp_month}-${c.exp_year}-${c.last_4}-${c.bin}`;
    if (!map.has(key)) map.set(key, c);
  });
  return Array.from(map.values());
}

export default function SubscriptionPlans() {
  const siteSettings = window.site || {};
  const siteConfig = siteSettings.config || {};
  const appSettings = window.app || {};
  const appStorage = appSettings.storage || localStorage;
  const token = appStorage.getItem('token') || localStorage.getItem('token');
  const appname = appSettings.appName || '';
  const environmentVariable = appSettings.appEnvName || 'dev';
  const lambdaUrl = siteSettings.screeningApiBaseUrl || siteSettings.submissionApiBaseUrl || '';

  const squareConfig = (siteConfig.paymentGateway || {}).squareUp || {};
  const appId = squareConfig.appId || '';
  const locationId = squareConfig.locationId || '';
  const isSandbox = appId.startsWith('sandbox');
  const currencySymbol = ((siteConfig.paymentGateway || {}).currency || {}).symbol || '$';

  const subscriptionPlans =
    (siteConfig.subscription || {}).subscriptionPlans?.plans || [];

  const storedFirst = appStorage.getItem('firstname') || '';
  const storedLast  = appStorage.getItem('lastname')  || '';
  const storedEmail = appStorage.getItem('email')     || '';

  const [selectedPlan, setSelectedPlan] = useState(null);
  const [step, setStep] = useState('plans');
  const [fdBilling, setFdBilling] = useState({
    firstName:      storedFirst,
    lastName:       storedLast,
    email:          storedEmail,
    cardholderName: `${storedFirst} ${storedLast}`.trim(),
    billingAddress: '',
    city:           '',
    zipCode:        '',
    country:        '',
    state:          '',
  });
  const [cards, setCards]               = useState([]);
  const [selectedCard, setSelectedCard] = useState('');
  const [processing, setProcessing]     = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [squareReady, setSquareReady]   = useState(false);
  const cardContainerRef = useRef(null);

  useEffect(() => {
    if (!appId || !locationId) return;
    const url = `https://${isSandbox ? 'sandbox.' : ''}web.squarecdn.com/v1/square.js`;
    affixScriptToHead(url).catch(() => {});
  }, []);

  useEffect(() => {
    if (step !== 'payment') return;
    loadCards();
    initSquare();
  }, [step]);

  const loadCards = () => {
    if (!lambdaUrl || !token) return;
    axios.post(`${lambdaUrl}/cardslist?appname=${appname}&env=${environmentVariable}&token=${token}`)
      .then(response => {
        if (response?.data?.statusCode === 200) {
          const saved = response?.data?.result?.[0]?.savedCards;
          if (saved?.length > 0) setCards(getUniqueCards(saved));
        }
      })
      .catch(() => {});
  };

  const initSquare = async () => {
    if (!appId || !locationId) return;
    const url = `https://${isSandbox ? 'sandbox.' : ''}web.squarecdn.com/v1/square.js`;
    try {
      await affixScriptToHead(url);
      if (!window.Square) return;
      squarePayments = window.Square.payments(appId, locationId);
      squareCard = await squarePayments.card({
        style: {
          'input': {
            color: '#ffffff',
            fontSize: '15px',
          },
          'input::placeholder': {
            color: 'rgba(255,255,255,0.4)',
          },
          '.message-text': {
            color: '#ffffff',
          },
          '.message-icon': {
            color: '#ffffff',
          },
        },
      });
      if (cardContainerRef.current) await squareCard.attach('#square-card-container');
      setSquareReady(true);
    } catch (err) {
      console.error('Square init error', err);
      setPaymentError('Payment form failed to load. Please refresh and try again.');
    }
  };

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    setStep('payment');
    setPaymentError('');
    setPaymentSuccess(false);
    setSquareReady(false);
    setSelectedCard('');
    setCards([]);
  };

  const billField = (field) => (e) =>
    setFdBilling(prev => ({ ...prev, [field]: e.target.value }));

  const buildPayload = (sourceToken) => ({
    activity: 'subscriptionPayment',
    token: sourceToken,
    planId: selectedPlan.planId || selectedPlan._id,
    planName: selectedPlan.planName,
    price: selectedPlan.price,
    cardholderName: fdBilling.cardholderName,
    billinginfo: {
      firstName:      fdBilling.firstName,
      lastName:       fdBilling.lastName,
      email:          fdBilling.email,
      billingAddress: fdBilling.billingAddress,
      city:           fdBilling.city,
      zipCode:        fdBilling.zipCode,
      country:        fdBilling.country,
      state:          fdBilling.state,
    },
    appname,
  });

  const doPayment = async (sourceToken) => {
    setProcessing(true);
    setPaymentError('');
    try {
      await axios.post(
        `${lambdaUrl}/paymentAuth?appname=${appname}&env=${environmentVariable}&token=${token}`,
        buildPayload(sourceToken),
        { headers: { 'content-type': 'application/json' } }
      );
      setPaymentSuccess(true);
    } catch (err) {
      console.error('Payment error', err);
      setPaymentError('Payment failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handlePayWithNewCard = async () => {
    if (!squareCard) { setPaymentError('Payment form not ready.'); return; }
    setProcessing(true);
    setPaymentError('');
    try {
      const result = await squareCard.tokenize();
      if (result.status !== 'OK') {
        setPaymentError(result.errors?.map(e => e.message).join(', ') || 'Tokenization failed.');
        setProcessing(false);
        return;
      }
      await doPayment(result.token);
    } catch (err) {
      console.error('Payment error', err);
      setPaymentError('Payment failed. Please try again.');
      setProcessing(false);
    }
  };

  const handlePayWithSavedCard = async () => {
    if (!selectedCard) return;
    await doPayment(selectedCard);
  };

  const getPeriodLabel = (plan) => {
    if (plan.period == '30') return '/mo';
    if (plan.period == '365' || plan.period == '12') return '/yr';
    return '';
  };

  const S = {
    page:           { backgroundColor: 'var(--bgColor)', minHeight: '100vh', paddingTop: '53px' },
    container:      { maxWidth: '1300px', margin: '0 auto', padding: '40px 24px' },
    heading:        { color: '#ffffff', fontSize: '32px', fontWeight: 700, textAlign: 'center', marginBottom: '8px' },
    subheading:     { color: '#8892a4', fontSize: '16px', textAlign: 'center', marginBottom: '48px' },
    plansGrid:      { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(480px, 1fr))', gap: '24px' },
    planCard:       { background: '#12152b', borderRadius: '16px', padding: '36px 40px 32px', display: 'flex', flexDirection: 'column' },
    planName:       { color: '#a78bfa', fontSize: '38px', fontWeight: 800, marginBottom: '6px', lineHeight: 1.1 },
    planPriceRow:   { display: 'flex', alignItems: 'baseline', gap: '6px', marginBottom: '28px' },
    planPriceAmt:   { color: '#fff', fontSize: '32px', fontWeight: 800, lineHeight: 1 },
    planPricePer:   { color: 'rgba(255,255,255,0.55)', fontSize: '14px', fontWeight: 400 },
    featuresGrid:   { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 32px', flex: 1, marginBottom: '32px' },
    featureCol:     { display: 'flex', flexDirection: 'column', gap: '6px' },
    featureItem:    { display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '6px 0' },
    checkCircle:    { width: '26px', height: '26px', borderRadius: '50%', background: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' },
    featureLabel:   { color: '#ffffff', fontSize: '14px', fontWeight: 500, lineHeight: '1.4' },
    subList:        { listStyle: 'none', padding: '6px 0 0 8px', margin: 0 },
    subItem:        { color: 'rgba(255,255,255,0.6)', fontSize: '13px', padding: '3px 0', display: 'flex', alignItems: 'center', gap: '8px' },
    subBullet:      { color: 'rgba(255,255,255,0.4)', fontSize: '16px', lineHeight: 1, flexShrink: 0 },
    selectBtn:      { alignSelf: 'flex-end', background: 'transparent', border: '1.5px solid rgba(255,255,255,0.35)', borderRadius: '40px', padding: '10px 32px', color: '#ffffff', fontSize: '13px', fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', cursor: 'pointer', transition: 'border-color 0.2s, background 0.2s' },
    // payment layout
    payLayout:      { display: 'grid', gridTemplateColumns: '1fr 1.6fr', gap: '32px', maxWidth: '1100px', margin: '0 auto' },
    // order summary (left)
    summaryCard:    { background: '#12152b', borderRadius: '16px', padding: '32px 28px', height: 'fit-content', position: 'sticky', top: '80px' },
    summaryHeading: { color: '#8892a4', fontSize: '11px', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '20px' },
    summaryPlanRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' },
    summaryPlanName:{ color: '#ffffff', fontSize: '17px', fontWeight: 700 },
    summaryPlanPrice:{ color: '#a78bfa', fontSize: '22px', fontWeight: 800 },
    changeLink:     { background: 'none', border: 'none', color: '#a78bfa', fontSize: '13px', cursor: 'pointer', padding: 0, textDecoration: 'underline' },
    summaryDivider: { borderColor: 'rgba(255,255,255,0.08)', margin: '16px 0' },
    summaryRow:     { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', color: '#8892a4', fontSize: '14px' },
    summaryTotal:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0 0', color: '#ffffff', fontSize: '16px', fontWeight: 700 },
    summaryTotalAmt:{ color: '#a78bfa', fontSize: '20px', fontWeight: 800 },
    // payment form (right)
    payCard:        { background: '#12152b', borderRadius: '16px', padding: '32px 28px' },
    payHeader:      { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' },
    payTitle:       { color: '#ffffff', fontSize: '16px', fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase' },
    cardBrands:     { display: 'flex', gap: '6px' },
    cardBrandBadge: { background: 'rgba(255,255,255,0.08)', borderRadius: '4px', padding: '3px 7px', color: 'rgba(255,255,255,0.6)', fontSize: '11px', fontWeight: 600 },
    formGrid2:      { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' },
    formGroup:      { marginBottom: '14px' },
    label:          { display: 'block', color: '#ffffff', fontSize: '12px', marginBottom: '5px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' },
    inputField:     { width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid #2a2f4a', borderRadius: '6px', outline: 'none', color: '#ffffff', fontSize: '14px', padding: '10px 12px', boxSizing: 'border-box' },
    squareBox:      {  borderRadius: '6px', padding: '2px 0', marginTop: '5px', minHeight: '44px', background: 'transparent', overflow: 'hidden' },
    orDivider:      { display: 'flex', alignItems: 'center', gap: '12px', margin: '20px 0' },
    orLine:         { flex: 1, borderTop: '1px solid rgba(255,255,255,0.1)' },
    orText:         { color: '#8892a4', fontSize: '12px', fontWeight: 500, letterSpacing: '1px' },
    savedCardsTitle:{ color: '#8892a4', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px' },
    savedCardRow:   { display: 'flex', alignItems: 'center', gap: '12px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px', padding: '10px 14px', marginBottom: '8px', cursor: 'pointer', transition: 'border-color 0.15s' },
    savedCardRowActive: { borderColor: '#a78bfa', background: 'rgba(167,139,250,0.08)' },
    savedCardBrand: { color: 'rgba(255,255,255,0.5)', fontSize: '10px', fontWeight: 700, background: 'rgba(255,255,255,0.08)', borderRadius: '3px', padding: '2px 6px', textTransform: 'uppercase' },
    savedCardNum:   { color: '#ffffff', fontSize: '14px', fontWeight: 600, flex: 1 },
    savedCardExp:   { color: '#8892a4', fontSize: '13px' },
    payWithCardBtn: { background: '#2B44FF', color: '#fff', border: 'none', borderRadius: '40px', padding: '9px 20px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', letterSpacing: '0.5px' },
    payBtn:         { background: '#2B44FF', color: '#fff', border: 'none', borderRadius: '40px', padding: '14px', width: '100%', cursor: 'pointer', fontWeight: 700, fontSize: '15px', letterSpacing: '1px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '20px' },
    spinner:        { display: 'inline-block', width: '16px', height: '16px', border: '2px solid rgba(255,255,255,.3)', borderRadius: '50%', borderTopColor: '#fff', animation: 'spin 1s linear infinite' },
    errBox:         { background: 'rgba(192,57,43,0.15)', border: '1px solid #c0392b', borderRadius: '6px', color: '#e74c3c', fontSize: '13px', padding: '10px 14px', marginBottom: '16px', marginTop: '8px' },
    successBox:     { background: 'rgba(26,127,75,0.15)', border: '1px solid #1a7f4b', borderRadius: '12px', padding: '40px 28px', textAlign: 'center', maxWidth: '480px', margin: '40px auto' },
    successTitle:   { color: '#ffffff', fontSize: '22px', fontWeight: 700, marginBottom: '8px' },
    successText:    { color: '#8892a4', fontSize: '15px' },
    backBtn:        { background: 'transparent', border: 'none', color: '#8892a4', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '14px', marginBottom: '28px', padding: 0 },
  };

  const plans = Array.isArray(subscriptionPlans) ? subscriptionPlans : [];

  const splitFeatures = (plan) => {
    const features = Array.isArray(plan.features) ? plan.features
      : Array.isArray(plan.description) ? plan.description.map(d => (typeof d === 'string' ? { label: d } : d))
      : [];
    const mid = 7;
    return [features.slice(0, mid), features.slice(mid)];
  };

  const FeatureItem = ({ feature }) => {
    const label = typeof feature === 'string' ? feature : feature.label || feature.name || feature.title || feature.text || '';
    const subItems = typeof feature === 'object'
      ? (feature.subFeatures || feature.items || feature.subItems || feature.children || feature.list || feature.subList || feature.details || feature.value || [])
      : [];
    const subArray = Array.isArray(subItems) ? subItems : typeof subItems === 'string' ? [subItems] : [];
    return (
      <div style={S.featureItem}>
        <div style={S.checkCircle}>
          <span className="material-symbols-outlined" style={{ fontSize: '15px', color: '#ffffff' }}>check</span>
        </div>
        <div>
          <div style={S.featureLabel}>{label}</div>
          {subArray.length > 0 && (
            <ol style={S.subList}>
              {subArray.map((sub, si) => (
                <li key={si} style={S.subItem}>
                  <span style={S.subBullet}>•</span>
                  {typeof sub === 'string' ? sub : sub.label || sub.name || sub.text || ''}
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    );
  };

  const subtotal = selectedPlan ? (parseFloat(selectedPlan.price) || 0) : 0;
  const tax = 0;
  const total = subtotal + tax;
  const fmtAmt = (n) => `${currencySymbol}${n.toFixed(2)}`;

  return (
    <div style={S.page}>
      <Header />
      <div style={S.container}>

        {step === 'plans' && (
          <>
            <h1 style={S.heading}>Choose Your Plan</h1>
            <p style={S.subheading}>Select the plan that works best for you</p>

            {plans.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#8892a4', padding: '60px 0' }}>
                No subscription plans available.
              </div>
            ) : (
              <div style={S.plansGrid}>
                {plans.map((plan, i) => {
                  const isFree = plan.planId === 'free' || plan.price === 0 || plan.price === '0';
                  const [leftCol, rightCol] = splitFeatures(plan);
                  const priceAmt = isFree ? 'Free' : `${currencySymbol}${plan.price}`;
                  const pricePer = plan.priceLabel || (getPeriodLabel(plan) ? 'per ' + (plan.period == '30' ? 'month' : 'year') : '');
                  return (
                    <div key={i} style={S.planCard}>
                      <div style={S.planName}>{plan.planName}</div>
                      <div style={S.planPriceRow}>
                        <span style={S.planPriceAmt}>{priceAmt}</span>
                        {pricePer && <span style={S.planPricePer}>{pricePer}</span>}
                      </div>

                      <div style={S.featuresGrid}>
                        <div style={S.featureCol}>
                          {leftCol.map((f, fi) => <FeatureItem key={fi} feature={f} />)}
                        </div>
                        <div style={S.featureCol}>
                          {rightCol.map((f, fi) => <FeatureItem key={fi} feature={f} />)}
                        </div>
                      </div>

                      <button
                        style={S.selectBtn}
                        disabled={!!plan.buttonDisable}
                        onClick={() => !plan.buttonDisable && handleSelectPlan(plan)}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = '#a78bfa'; e.currentTarget.style.color = '#a78bfa'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.35)'; e.currentTarget.style.color = '#ffffff'; }}
                      >
                        SELECT PLAN
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {step === 'payment' && selectedPlan && (
          <>
            <button style={S.backBtn} onClick={() => { setStep('plans'); setSquareReady(false); setPaymentError(''); setCards([]); }}>
              <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>arrow_back</span>
              Back to Plans
            </button>

            {paymentSuccess ? (
              <div style={S.successBox}>
                <span className="material-symbols-outlined" style={{ fontSize: '56px', color: '#1a7f4b', display: 'block', marginBottom: '12px' }}>check_circle</span>
                <div style={S.successTitle}>Payment Successful!</div>
                <p style={S.successText}>
                  You're now subscribed to <strong>{selectedPlan.planName}</strong>. Enjoy your access!
                </p>
                <button style={{ ...S.payBtn, marginTop: '24px' }} onClick={() => { window.location = `/?appname=${appname}`; }}>
                  Go to Home
                </button>
              </div>
            ) : (
              <div style={S.payLayout}>

                {/* LEFT — ORDER SUMMARY */}
                <div style={S.summaryCard}>
                  <div style={S.summaryHeading}>Order Summary</div>

                  <div style={S.summaryPlanRow}>
                    <div>
                      <div style={S.summaryPlanName}>{selectedPlan.planName}</div>
                      <div style={{ color: '#8892a4', fontSize: '13px', marginTop: '2px' }}>
                        {selectedPlan.priceLabel || (getPeriodLabel(selectedPlan) ? (selectedPlan.period == '30' ? 'Monthly' : 'Yearly') + ' plan' : '')}
                      </div>
                    </div>
                    <button style={S.changeLink} onClick={() => { setStep('plans'); setCards([]); }}>Change</button>
                  </div>

                  <div style={{ ...S.summaryPlanPrice, marginTop: '8px' }}>
                    {currencySymbol}{selectedPlan.price}{getPeriodLabel(selectedPlan)}
                  </div>

                  <hr style={S.summaryDivider} />

                  <div style={S.summaryRow}>
                    <span>Subtotal</span>
                    <span style={{ color: '#ffffff' }}>{fmtAmt(subtotal)}</span>
                  </div>
                  <div style={S.summaryRow}>
                    <span>Tax</span>
                    <span style={{ color: '#ffffff' }}>{fmtAmt(tax)}</span>
                  </div>

                  <hr style={S.summaryDivider} />

                  <div style={S.summaryTotal}>
                    <span>Total</span>
                    <span style={S.summaryTotalAmt}>{fmtAmt(total)}</span>
                  </div>
                </div>

                {/* RIGHT — PAYMENT FORM */}
                <div style={S.payCard}>
                  <div style={S.payHeader}>
                    <div style={S.payTitle}>Payment</div>
                    <div style={S.cardBrands}>
                      <span style={S.cardBrandBadge}>Visa</span>
                      <span style={S.cardBrandBadge}>AmEx</span>
                      <span style={S.cardBrandBadge}>MC</span>
                    </div>
                  </div>

                  {/* Name row */}
                  <div style={{ ...S.formGrid2, marginBottom: '14px' }}>
                    <div>
                      <label style={S.label}>First Name</label>
                      <input style={S.inputField} type="text" value={fdBilling.firstName} onChange={billField('firstName')} placeholder="First name" />
                    </div>
                    <div>
                      <label style={S.label}>Last Name</label>
                      <input style={S.inputField} type="text" value={fdBilling.lastName} onChange={billField('lastName')} placeholder="Last name" />
                    </div>
                  </div>

                  <div style={S.formGroup}>
                    <label style={S.label}>Email</label>
                    <input style={S.inputField} type="email" value={fdBilling.email} onChange={billField('email')} placeholder="Email address" />
                  </div>

                  <div style={S.formGroup}>
                    <label style={S.label}>Name on Card</label>
                    <input style={S.inputField} type="text" value={fdBilling.cardholderName} onChange={billField('cardholderName')} placeholder="Name on card" />
                  </div>

                  <div style={S.formGroup}>
                    <label style={S.label}>Billing Address</label>
                    <input style={S.inputField} type="text" value={fdBilling.billingAddress} onChange={billField('billingAddress')} placeholder="Street address" />
                  </div>

                  <div style={{ ...S.formGrid2, marginBottom: '14px' }}>
                    <div>
                      <label style={S.label}>City</label>
                      <input style={S.inputField} type="text" value={fdBilling.city} onChange={billField('city')} placeholder="City" />
                    </div>
                    <div>
                      <label style={S.label}>Postal Code</label>
                      <input style={S.inputField} type="text" value={fdBilling.zipCode} onChange={billField('zipCode')} placeholder="Postal code" />
                    </div>
                  </div>

                  <div style={{ ...S.formGrid2, marginBottom: '20px' }}>
                    <div>
                      <label style={S.label}>Country</label>
                      <input style={S.inputField} type="text" value={fdBilling.country} onChange={billField('country')} placeholder="Country" />
                    </div>
                    <div>
                      <label style={S.label}>State / Province</label>
                      <input style={S.inputField} type="text" value={fdBilling.state} onChange={billField('state')} placeholder="State / Province" />
                    </div>
                  </div>

                  {/* Square card field */}
                  <div>
                    <label style={S.label}>Card Details</label>
                    <div id="square-card-container" style={S.squareBox} ref={cardContainerRef}>
                      {!squareReady && (
                        <div style={{ color: '#6f7685', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={S.spinner} />
                          Loading payment form...
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Saved cards */}
                  {cards.length > 0 && (
                    <>
                      <div style={S.orDivider}>
                        <div style={S.orLine} />
                        <span style={S.orText}>OR USE SAVED CARD</span>
                        <div style={S.orLine} />
                      </div>
                      <div style={S.savedCardsTitle}>Saved Cards</div>
                      {cards.map(c => {
                        const isActive = selectedCard === c.id;
                        return (
                          <div
                            key={c.id}
                            style={{ ...S.savedCardRow, ...(isActive ? S.savedCardRowActive : {}) }}
                            onClick={() => setSelectedCard(isActive ? '' : c.id)}
                          >
                            <input
                              type="radio"
                              name="savedCard"
                              value={c.id}
                              checked={isActive}
                              onChange={() => setSelectedCard(c.id)}
                              style={{ accentColor: '#a78bfa', cursor: 'pointer' }}
                            />
                            <span style={S.savedCardBrand}>{c.card_brand || 'CARD'}</span>
                            <span style={S.savedCardNum}>**** **** **** {c.last_4}</span>
                            <span style={S.savedCardExp}>{c.exp_month}/{c.exp_year}</span>
                            {isActive && (
                              <button
                                style={S.payWithCardBtn}
                                onClick={(e) => { e.stopPropagation(); handlePayWithSavedCard(); }}
                                disabled={processing}
                              >
                                {processing ? 'Processing...' : 'Pay'}
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </>
                  )}

                  {paymentError && <div style={S.errBox}>{paymentError}</div>}

                  {(!appId || !locationId) && (
                    <div style={S.errBox}>Payment gateway is not configured. Please contact support.</div>
                  )}

                  {/* Pay with new card button */}
                  {!selectedCard && (
                    <button
                      style={{ ...S.payBtn, opacity: processing || !squareReady ? 0.7 : 1 }}
                      onClick={handlePayWithNewCard}
                      disabled={processing || !squareReady || !appId || !locationId}
                    >
                      {processing && <span style={S.spinner} />}
                      {processing ? 'Processing...' : `Pay ${fmtAmt(total)}`}
                    </button>
                  )}

                  <p style={{ color: '#6f7685', fontSize: '12px', textAlign: 'center', marginTop: '14px' }}>
                    <span className="material-symbols-outlined" style={{ fontSize: '14px', verticalAlign: 'middle', marginRight: '4px' }}>lock</span>
                    Secured by Square. Your card info is never stored on our servers.
                  </p>
                </div>

              </div>
            )}
          </>
        )}

      </div>
    </div>
  );
}
